/**
 * Bookings page — aircraft reservations with modal-based editing.
 * Now uses unified flights table (booking + flight log in one row).
 *
 * Requirements: 12.1, 12.2, 12.3, 12.4, 12.5, 13.1
 */

import { createDataGrid } from '../components/data-grid.js';
import { openDetailModal } from '../components/detail-modal.js';
import { openFlightLogModal } from '../components/flight-log-modal.js';
import { renderBookingCalendar, filterBookingsByAircraft, CALENDAR_VIEW, getMonday } from '../components/booking-calendar.js';
import {
  getFlights,
  createFlight,
  updateFlight,
  transitionFlightStatus,
  getAircraft,
  getBillingRates,
} from '../services/api.js';
import { getRole, getUser, isAdmin, isOwner } from '../services/auth.js';
import { formatDateUK } from '../utils/format.js';
import { FLIGHT_STATUS } from '../utils/constants.js';

// ── Exported pure helpers for testability ─────────────

/**
 * Format a booking for display in the grid.
 * @param {Object} booking
 * @returns {Object} Formatted booking with display-friendly fields
 */
export function formatBookingForDisplay(booking) {
  if (!booking) return {};
  return {
    ...booking,
    display_date: booking.flight_date ? formatDateUK(booking.flight_date) : '—',
    display_aircraft: booking.aircraft?.registration || '—',
    display_pilot: booking.pilots?.name || '—',
    display_start: (booking.start_time || '').slice(0, 5),
    display_end: (booking.end_time || '').slice(0, 5),
    display_status: booking.status || '—',
    display_purpose: booking.purpose || '—',
    display_end_date: booking.end_date ? formatDateUK(booking.end_date) : '',
  };
}

/**
 * Returns true if the booking can be edited (status is SUBMITTED).
 * @param {Object} booking
 * @returns {boolean}
 */
export function canEditBooking(booking) {
  if (!booking || !booking.status) return false;
  return booking.status === FLIGHT_STATUS.SUBMITTED;
}

/**
 * Returns true if the booking can be cancelled (SUBMITTED or APPROVED, and start is in the future).
 * @param {Object} booking
 * @returns {boolean}
 */
export function canCancelBooking(booking) {
  if (!booking || !booking.flight_date || !booking.start_time) return false;
  if (booking.status !== FLIGHT_STATUS.SUBMITTED && booking.status !== FLIGHT_STATUS.APPROVED) return false;
  const startDT = new Date(`${booking.flight_date}T${booking.start_time}`);
  return startDT > new Date();
}

/**
 * Filter bookings by role. Pilots see only their own; admins/owners see all.
 * @param {Object[]} bookings
 * @param {string} role
 * @param {string} userId
 * @returns {Object[]}
 */
export function filterBookingsByRole(bookings, role, userId) {
  if (!Array.isArray(bookings)) return [];
  if (role === 'admin' || role === 'owner') return bookings;
  return bookings.filter((b) => b.pilot_id === userId);
}

/**
 * Check if a new booking overlaps with any existing active bookings
 * for the same aircraft on the same date.
 * @param {Object} newBooking - { aircraft_id, flight_date, start_time, end_time }
 * @param {Object[]} existingBookings
 * @returns {boolean}
 */
export function checkBookingOverlap(newBooking, existingBookings) {
  if (!Array.isArray(existingBookings) || !newBooking) return false;
  const checkDate = newBooking.flight_date || newBooking.date;
  return existingBookings.some((existing) => {
    if (existing.aircraft_id !== newBooking.aircraft_id) return false;
    if (existing.flight_date !== checkDate) return false;
    if (existing.status === FLIGHT_STATUS.CANCELLED || existing.status === FLIGHT_STATUS.REJECTED) return false;
    return newBooking.start_time < existing.end_time && newBooking.end_time > existing.start_time;
  });
}

/**
 * Get the status badge class for a flight/booking status.
 * @param {string} status
 * @returns {string}
 */
export function getStatusBadgeClass(status) {
  switch (status) {
    case FLIGHT_STATUS.SUBMITTED: return 'badge-blue';
    case FLIGHT_STATUS.APPROVED: return 'badge-green';
    case FLIGHT_STATUS.REJECTED: return 'badge-red';
    case FLIGHT_STATUS.CANCELLED: return 'badge-grey';
    case FLIGHT_STATUS.FLOWN: return 'badge-purple';
    default: return '';
  }
}

// ── Page state ────────────────────────────────────────

let aircraftList = [];
let allBookings = [];
let billingRatesList = [];
let calendarYear = new Date().getFullYear();
let calendarMonth = new Date().getMonth();
let calendarFilterAircraftId = null;
let calendarMyAircraftChecked = false;
let calendarMyFlightsChecked = false;
let calendarViewMode = CALENDAR_VIEW.MONTH;
let calendarWeekStart = getMonday(new Date());
let calendarDayDate = new Date().toISOString().split('T')[0];

/** Persistent checkbox element — survives grid rebuilds */
let _myBookingsCheckbox = null;

// ── Main render ───────────────────────────────────────

export async function renderBookings() {
  const app = document.getElementById('app');
  if (!app) return;

  app.innerHTML = `
    <div class="container">
      <h2>Flights</h2>
      <div id="bookings-msg"></div>
      <div id="pending-approvals-banner"></div>
      <div class="card">
        <h3 id="calendar-heading" style="margin-bottom:1rem;">Fleet Calendar</h3>
        <div id="calendar-section"></div>
      </div>
      <div class="card" style="margin-top:1rem;">
        <h3>Your Flights</h3>
        <div id="bookings-grid-section">Loading…</div>
      </div>
    </div>
  `;

  try {
    aircraftList = await getAircraft();
    try { billingRatesList = await getBillingRates(); } catch { billingRatesList = []; }
    await loadBookings();
    renderFleetCalendar();

    // Owner: show pending-approvals banner
    if (isOwner()) {
      const allFlights = await getFlights({});
      const pendingCount = allFlights.filter(f => f.status === 'SUBMITTED').length;
      const bannerEl = document.getElementById('pending-approvals-banner');
      if (bannerEl && pendingCount > 0) {
        bannerEl.innerHTML = `
          <div class="pending-approvals-banner">
            <span>⚠️ <strong>${pendingCount}</strong> booking${pendingCount === 1 ? '' : 's'} pending your approval.</span>
            <a href="#/owner/bookings" class="btn btn--primary btn--sm">Review Bookings</a>
          </div>
        `;
      }
    }

    // Auto-open New Booking modal if navigated from dashboard quick action
    if (sessionStorage.getItem('auto-open-modal') === 'true') {
      sessionStorage.removeItem('auto-open-modal');
      openNewBookingModal();
    }
  } catch (err) {
    showMsg(`Failed to load bookings: ${err.message}`, true);
  }
}

// ── New Booking modal ─────────────────────────────────

function getAircraftOptions() {
  return aircraftList
    .filter((ac) => {
      if (ac.meta && ac.meta.retired) return false;
      if (ac.name && ac.name.includes('[RETIRED]')) return false;
      return true;
    })
    .map((ac) => ({ value: ac.id, label: `${ac.registration} (${ac.type})` }));
}

function buildBookingFields() {
  const user = getUser();
  const captainName = user?.user_metadata?.name || user?.email || '';
  return [
    { name: 'aircraft_id', label: 'Aircraft', type: 'select', options: getAircraftOptions(), required: true },
    { name: 'captain_display', label: 'Captain (PIC)', type: 'text', attrs: { readonly: 'readonly' } },
    { name: 'flight_date', label: 'Start Date', type: 'date', required: true },
    { name: 'start_time', label: 'Start Time', type: 'time', required: true },
    { name: 'end_date', label: 'End Date', type: 'date', required: true },
    { name: 'end_time', label: 'End Time', type: 'time', required: true },
    { name: 'departure', label: 'From (Airfield)', type: 'text', placeholder: 'e.g. EGBJ' },
    { name: 'destination', label: 'To (Airfield)', type: 'text', placeholder: 'e.g. EGTK' },
    { name: 'expected_passengers', label: 'Expected Passengers', type: 'number', placeholder: '0' },
    { name: 'estimated_cost', label: 'Estimated Cost', type: 'text', attrs: { readonly: 'readonly' }, placeholder: '—' },
    { name: 'purpose', label: 'Purpose', type: 'textarea', placeholder: 'e.g. Cross-country nav exercise', fullWidth: true },
  ];
}

function getAircraftRate(aircraftId) {
  const rates = billingRatesList.filter(r => r.aircraft_id === aircraftId);
  if (rates.length === 0) return null;
  // Pick the most recent effective_from
  rates.sort((a, b) => (b.effective_from || '').localeCompare(a.effective_from || ''));
  return rates[0];
}

function computeDurationHours(startDate, startTime, endDate, endTime) {
  if (!startDate || !startTime || !endDate || !endTime) return 0;
  const start = new Date(`${startDate}T${startTime}`);
  const end = new Date(`${endDate}T${endTime}`);
  const ms = end - start;
  if (ms <= 0) return 0;
  return ms / (1000 * 60 * 60);
}

function wireBookingModalInteractions() {
  requestAnimationFrame(() => {
    const aircraftSel = document.querySelector('[name="aircraft_id"]');
    const startDate = document.querySelector('[name="flight_date"]');
    const startTime = document.querySelector('[name="start_time"]');
    const endDate = document.querySelector('[name="end_date"]');
    const endTime = document.querySelector('[name="end_time"]');
    const estCost = document.querySelector('[name="estimated_cost"]');
    const captainDisplay = document.querySelector('[name="captain_display"]');

    const user = getUser();
    if (captainDisplay) {
      captainDisplay.value = user?.user_metadata?.name || user?.email || '';
    }

    const recalcEnd = () => {
      if (!startDate?.value || !startTime?.value) return;
      if (endDate && !endDate.value) endDate.value = startDate.value;
      if (endTime && !endTime.value) {
        const [h, m] = startTime.value.split(':').map(Number);
        const total = (h * 60 + m + 60) % (24 * 60);
        endTime.value = `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
      }
      recalcCost();
    };
    const recalcCost = () => {
      if (!estCost || !aircraftSel) return;
      const rate = getAircraftRate(aircraftSel.value);
      const hours = computeDurationHours(startDate?.value, startTime?.value, endDate?.value, endTime?.value);
      if (!rate || hours <= 0) {
        estCost.value = rate ? '—' : 'No rate configured';
        return;
      }
      const cost = hours * Number(rate.rate_per_hour);
      estCost.value = `£${cost.toFixed(2)} (${hours.toFixed(2)}h × £${Number(rate.rate_per_hour).toFixed(2)}, ${rate.billing_basis})`;
    };

    startTime?.addEventListener('change', recalcEnd);
    startDate?.addEventListener('change', recalcEnd);
    endTime?.addEventListener('change', recalcCost);
    endDate?.addEventListener('change', recalcCost);
    aircraftSel?.addEventListener('change', recalcCost);
  });
}

function openNewBookingModal() {
  openDetailModal({
    title: 'New Booking',
    fields: buildBookingFields(),
    onSave: async (formData) => {
      const user = getUser();
      const payload = {
        aircraft_id: formData.aircraft_id,
        pilot_id: user.id,
        flight_date: formData.flight_date,
        start_time: formData.start_time,
        end_date: formData.end_date || formData.flight_date,
        end_time: formData.end_time,
        departure: formData.departure || null,
        destination: formData.destination || null,
        expected_passengers: parseInt(formData.expected_passengers, 10) || 0,
        who_pays: 'CAPTAIN',
        purpose: formData.purpose || '',
        status: FLIGHT_STATUS.SUBMITTED,
      };

      // Client-side overlap check
      const hasOverlap = checkBookingOverlap(
        { aircraft_id: payload.aircraft_id, flight_date: payload.flight_date, start_time: payload.start_time, end_time: payload.end_time },
        allBookings,
      );
      if (hasOverlap) {
        throw new Error('This booking overlaps with an existing booking for the same aircraft.');
      }

      await createFlight(payload);
      showMsg('Booking created successfully.');
      await loadBookings();
      renderFleetCalendar();
    },
  });
  wireBookingModalInteractions();
}

// ── Edit Booking modal (only SUBMITTED) ───────────────

function openEditBookingModal(booking) {
  if (!canEditBooking(booking)) {
    showMsg('Only submitted bookings can be edited.', true);
    return;
  }

  const fields = [
    { name: 'aircraft_id', label: 'Aircraft', type: 'select', options: getAircraftOptions(), required: true },
    { name: 'who_pays', label: 'Who Pays', type: 'select', options: [
      { value: 'CAPTAIN', label: 'Captain' },
      { value: 'P2', label: 'P2' },
    ], required: true },
    { name: 'flight_date', label: 'Start Date', type: 'date', required: true },
    { name: 'start_time', label: 'Start Time', type: 'time', required: true },
    { name: 'end_date', label: 'End Date', type: 'date', required: true },
    { name: 'end_time', label: 'End Time', type: 'time', required: true },
    { name: 'departure', label: 'From (Airfield)', type: 'text', placeholder: 'e.g. EGBJ' },
    { name: 'destination', label: 'To (Airfield)', type: 'text', placeholder: 'e.g. EGTK' },
    { name: 'expected_passengers', label: 'Expected Passengers', type: 'number' },
    { name: 'purpose', label: 'Purpose', type: 'text' },
  ];

  openDetailModal({
    title: 'Edit Booking',
    fields,
    values: {
      aircraft_id: booking.aircraft_id,
      who_pays: booking.who_pays || 'CAPTAIN',
      flight_date: booking.flight_date,
      start_time: (booking.start_time || '').slice(0, 5),
      end_date: booking.end_date || booking.flight_date,
      end_time: (booking.end_time || '').slice(0, 5),
      departure: booking.departure || '',
      destination: booking.destination || '',
      expected_passengers: String(booking.expected_passengers || 0),
      purpose: booking.purpose || '',
    },
    onSave: async (formData) => {
      await updateFlight(booking.id, {
        aircraft_id: formData.aircraft_id,
        who_pays: formData.who_pays || 'CAPTAIN',
        flight_date: formData.flight_date,
        start_time: formData.start_time,
        end_date: formData.end_date || formData.flight_date,
        end_time: formData.end_time,
        departure: formData.departure || null,
        destination: formData.destination || null,
        expected_passengers: parseInt(formData.expected_passengers, 10) || 0,
        purpose: formData.purpose || '',
      });

      showMsg('Booking updated successfully.');
      await loadBookings();
      renderFleetCalendar();
    },
  });
}

// ── Cancel booking handler ────────────────────────────

async function handleCancelBooking(bookingId) {
  const booking = allBookings.find((b) => b.id === bookingId);
  if (!booking) return;

  if (!canCancelBooking(booking)) {
    showMsg('Cannot cancel this booking.', true);
    return;
  }

  try {
    await transitionFlightStatus(bookingId, FLIGHT_STATUS.CANCELLED);
    showMsg('Booking cancelled.');
    await loadBookings();
    renderFleetCalendar();
  } catch (err) {
    showMsg(`Failed to cancel booking: ${err.message}`, true);
  }
}

// ── Load and render bookings grid ─────────────────────

async function loadBookings() {
  const container = document.getElementById('bookings-grid-section');
  if (!container) return;

  try {
    // Fetch all flights (all statuses) — unified table
    const raw = await getFlights();
    const role = getRole();
    const user = getUser();
    allBookings = raw;

    // Create persistent checkbox once
    if (!_myBookingsCheckbox) {
      _myBookingsCheckbox = document.createElement('input');
      _myBookingsCheckbox.type = 'checkbox';
      _myBookingsCheckbox.id = 'my-bookings-filter';
    }

    // For the bookings grid, show only booking-phase statuses (not FLOWN)
    const bookingPhase = raw.filter((b) =>
      b.status === FLIGHT_STATUS.SUBMITTED ||
      b.status === FLIGHT_STATUS.APPROVED ||
      b.status === FLIGHT_STATUS.REJECTED ||
      b.status === FLIGHT_STATUS.CANCELLED
    );
    const visible = filterBookingsByRole(bookingPhase, role, user?.id);
    // If checkbox is checked, filter to user's bookings
    const data = _myBookingsCheckbox.checked
      ? visible.filter((b) => b.pilot_id === user?.id)
      : visible;
    renderBookingsGrid(container, data);
  } catch (err) {
    container.innerHTML = `<p class="error">Failed to load bookings: ${err.message}</p>`;
  }
}

function renderBookingsGrid(container, bookings) {
  container.innerHTML = '';

  const role = getRole();
  const user = getUser();

  const columns = [
    { key: 'flight_date', label: 'Start Date', format: (v) => formatDateUK(v) },
    {
      key: 'aircraft_reg',
      label: 'Aircraft',
      html: true,
      format: (_, row) => {
        const reg = row.aircraft?.registration;
        const id = row.aircraft?.id || row.aircraft_id;
        if (!reg) return '—';
        if (!id) return reg;
        return `<a href="#/aircraft/${id}" class="aircraft-link">${reg}</a>`;
      },
    },
    { key: 'start_time', label: 'Start', format: (v) => (v || '').slice(0, 5) },
    { key: 'end_time', label: 'End', format: (v) => (v || '').slice(0, 5) },
    { key: 'purpose', label: 'Purpose', format: (v) => v || '—' },
  ];

  if (isAdmin()) {
    columns.push({
      key: 'pilot_name',
      label: 'Pilot',
      format: (_, row) => row.pilots?.name || '—',
    });
  }

  columns.push({
    key: 'status',
    label: 'Status',
    html: true,
    format: (v) => {
      const cls = getStatusBadgeClass(v);
      return `<span class="badge ${cls}">${v || '—'}</span>`;
    },
  });

  // Build actions array for edit/cancel icon buttons
  const actions = [
    {
      label: 'Edit',
      className: 'data-grid__action-btn--edit',
      onClick: (row) => {
        if (canEditBooking(row)) {
          openEditBookingModal(row);
        } else {
          showMsg('Only submitted bookings can be edited.', true);
        }
      },
    },
    {
      label: 'Cancel',
      className: 'data-grid__action-btn--delete',
      onClick: (row) => {
        if (canCancelBooking(row)) {
          handleCancelBooking(row.id);
        } else {
          showMsg('Cannot cancel this booking.', true);
        }
      },
    },
  ];

  const grid = createDataGrid({
    columns,
    data: bookings,
    defaultSort: 'flight_date',
    defaultOrder: 'desc',
    emptyMessage: 'No bookings found',
    filterable: true,
    actions,
  });

  // Inject toolbar: filter + My Bookings + New Booking on one line
  const filterInput = grid.querySelector('.data-grid__filter');
  if (filterInput) {
    const toolbar = document.createElement('div');
    toolbar.className = 'data-grid__toolbar';

    const left = document.createElement('div');
    left.className = 'data-grid__toolbar-left';
    filterInput.parentNode.insertBefore(toolbar, filterInput);
    left.appendChild(filterInput);

    // My Bookings checkbox — persistent element, survives grid rebuilds
    if (_myBookingsCheckbox) {
      const lbl = document.createElement('label');
      lbl.className = 'aircraft-list__filter-toggle';
      lbl.appendChild(_myBookingsCheckbox);
      lbl.appendChild(document.createTextNode(' My Flights'));
      left.appendChild(lbl);

      _myBookingsCheckbox.onchange = () => {
        if (_myBookingsCheckbox.checked) {
          const mine = allBookings
            .filter((b) => b.pilot_id === user?.id && b.status !== FLIGHT_STATUS.FLOWN);
          renderBookingsGrid(container, mine);
        } else {
          const bookingPhase = allBookings.filter((b) => b.status !== FLIGHT_STATUS.FLOWN);
          const visible = filterBookingsByRole(bookingPhase, role, user?.id);
          renderBookingsGrid(container, visible);
        }
      };
    }

    toolbar.appendChild(left);

    // New Booking button (right side)
    const addBtn = document.createElement('button');
    addBtn.className = 'btn btn--primary';
    addBtn.textContent = '+ New Booking';
    addBtn.addEventListener('click', () => openNewBookingModal());
    toolbar.appendChild(addBtn);

    // Remove any default add button from data-grid
    const defaultAddBtn = grid.querySelector('.data-grid__add-btn');
    if (defaultAddBtn) defaultAddBtn.remove();
  }

  container.appendChild(grid);
}

// ── Fleet calendar rendering ──────────────────────────

function renderFleetCalendar() {
  const section = document.getElementById('calendar-section');
  if (!section) return;
  section.innerHTML = '';

  const activeAircraft = aircraftList.filter((ac) => {
    if (ac.meta?.retired) return false;
    if (ac.name?.includes('[RETIRED]')) return false;
    return true;
  });

  if (activeAircraft.length === 0) {
    section.innerHTML = '<p class="text-muted">No active aircraft to display.</p>';
    return;
  }

  const role = getRole();
  const user = getUser();

  // Filter bookings for "My Flights" checkbox
  let calBookings = allBookings;
  if (calendarMyFlightsChecked && user) {
    calBookings = calBookings.filter((b) => b.pilot_id === user.id);
  }

  // Filter aircraft for "My Aircraft" checkbox
  let calAircraft = activeAircraft;
  if (calendarMyAircraftChecked && user) {
    calAircraft = activeAircraft.filter((ac) => ac.meta?.owner_id === user.id);
    // Also filter bookings to only those aircraft
    const myAcIds = new Set(calAircraft.map((ac) => ac.id));
    calBookings = calBookings.filter((b) => myAcIds.has(b.aircraft_id));
  }

  const calendar = renderBookingCalendar({
    aircraft: calAircraft,
    bookings: calBookings,
    year: calendarYear,
    month: calendarMonth,
    viewMode: calendarViewMode,
    weekStart: calendarWeekStart,
    dayDate: calendarDayDate,
    selectedAircraftId: calendarFilterAircraftId,
    onFilterChange: (aircraftId) => {
      calendarFilterAircraftId = aircraftId;
      renderFleetCalendar();
    },
    onViewChange: (mode) => {
      calendarViewMode = mode;
      // Always show current week / today when switching views
      if (mode === CALENDAR_VIEW.WEEK) {
        const today = new Date();
        calendarWeekStart = getMonday(today);
        calendarYear = today.getFullYear();
        calendarMonth = today.getMonth();
      } else if (mode === CALENDAR_VIEW.DAY) {
        const today = new Date();
        calendarDayDate = today.toISOString().split('T')[0];
        calendarYear = today.getFullYear();
        calendarMonth = today.getMonth();
      }
      renderFleetCalendar();
    },
    showMyAircraft: role === 'owner',
    myAircraftChecked: calendarMyAircraftChecked,
    onMyAircraftChange: (checked) => {
      calendarMyAircraftChecked = checked;
      renderFleetCalendar();
    },
    showMyFlights: role === 'owner' || role === 'pilot',
    myFlightsChecked: calendarMyFlightsChecked,
    onMyFlightsChange: (checked) => {
      calendarMyFlightsChecked = checked;
      renderFleetCalendar();
    },
    onDayClick: (dateStr) => {
      if (calendarViewMode === CALENDAR_VIEW.MONTH) {
        // In month view, clicking a day drills into day view
        calendarViewMode = CALENDAR_VIEW.DAY;
        calendarDayDate = dateStr;
        renderFleetCalendar();
        return;
      }
      if (calendarViewMode === CALENDAR_VIEW.WEEK) {
        // In week view, clicking a day drills into day view
        calendarViewMode = CALENDAR_VIEW.DAY;
        calendarDayDate = dateStr;
        renderFleetCalendar();
        return;
      }
      // In day view, clicking opens new booking modal for future dates
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const clicked = new Date(dateStr + 'T12:00:00');
      if (clicked >= today) {
        openNewBookingModalWithDate(dateStr);
      }
    },
    onBookingClick: (booking) => {
      const todayStr = new Date().toISOString().split('T')[0];

      // If this flight needs logging, open the flight log modal directly
      if (booking.status === FLIGHT_STATUS.APPROVED && booking.flight_date <= todayStr && booking.flight_time_mins == null) {
        openFlightLogModal({
          flight: booking,
          onSaved: async () => {
            showMsg('Flight logged successfully.');
            await loadBookings();
            renderFleetCalendar();
          },
        });
        return;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const bookingDate = new Date(booking.flight_date + 'T12:00:00');

      if (bookingDate >= today) {
        openBookingDetailModal(booking);
      } else {
        showFlightLogForBooking(booking);
      }
    },
    onPrev: () => {
      if (calendarViewMode === CALENDAR_VIEW.MONTH) {
        calendarMonth--;
        if (calendarMonth < 0) { calendarMonth = 11; calendarYear--; }
      } else if (calendarViewMode === CALENDAR_VIEW.WEEK) {
        calendarWeekStart = new Date(calendarWeekStart);
        calendarWeekStart.setDate(calendarWeekStart.getDate() - 7);
      } else {
        const d = new Date(calendarDayDate + 'T12:00:00');
        d.setDate(d.getDate() - 1);
        calendarDayDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      }
      renderFleetCalendar();
    },
    onNext: () => {
      if (calendarViewMode === CALENDAR_VIEW.MONTH) {
        calendarMonth++;
        if (calendarMonth > 11) { calendarMonth = 0; calendarYear++; }
      } else if (calendarViewMode === CALENDAR_VIEW.WEEK) {
        calendarWeekStart = new Date(calendarWeekStart);
        calendarWeekStart.setDate(calendarWeekStart.getDate() + 7);
      } else {
        const d = new Date(calendarDayDate + 'T12:00:00');
        d.setDate(d.getDate() + 1);
        calendarDayDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      }
      renderFleetCalendar();
    },
  });

  // Update the card heading with the date context
  const heading = document.getElementById('calendar-heading');
  if (heading) {
    let suffix = '';
    if (calendarViewMode === CALENDAR_VIEW.MONTH) {
      suffix = new Date(calendarYear, calendarMonth, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    } else if (calendarViewMode === CALENDAR_VIEW.WEEK) {
      const thu = new Date(calendarWeekStart);
      thu.setDate(thu.getDate() + 3);
      const yearStart = new Date(thu.getFullYear(), 0, 1);
      const weekNum = Math.ceil(((thu - yearStart) / 86400000 + 1) / 7);
      suffix = `${thu.getFullYear()}w${String(weekNum).padStart(2, '0')}`;
    } else {
      const dayObj = new Date(calendarDayDate + 'T12:00:00');
      suffix = dayObj.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
    }
    heading.textContent = `Fleet Calendar - ${suffix}`;
  }

  section.appendChild(calendar);
}

/**
 * Open new booking modal pre-populated with a date from the calendar.
 * @param {string} dateStr - YYYY-MM-DD
 */
function openNewBookingModalWithDate(dateStr) {
  openDetailModal({
    title: 'New Booking',
    fields: buildBookingFields(),
    values: {
      flight_date: dateStr,
      end_date: dateStr,
    },
    onSave: async (formData) => {
      const user = getUser();
      const payload = {
        aircraft_id: formData.aircraft_id,
        pilot_id: user.id,
        flight_date: formData.flight_date,
        start_time: formData.start_time,
        end_date: formData.end_date || formData.flight_date,
        end_time: formData.end_time,
        departure: formData.departure || null,
        destination: formData.destination || null,
        expected_passengers: parseInt(formData.expected_passengers, 10) || 0,
        who_pays: 'CAPTAIN',
        purpose: formData.purpose || '',
        status: FLIGHT_STATUS.SUBMITTED,
      };

      const hasOverlap = checkBookingOverlap(
        { aircraft_id: payload.aircraft_id, flight_date: payload.flight_date, start_time: payload.start_time, end_time: payload.end_time },
        allBookings,
      );
      if (hasOverlap) {
        throw new Error('This booking overlaps with an existing booking for the same aircraft.');
      }

      await createFlight(payload);
      showMsg('Booking created successfully.');
      await loadBookings();
      renderFleetCalendar();
    },
  });
  wireBookingModalInteractions();
}

// ── Booking detail modal (future bookings) ────────────

function openBookingDetailModal(booking) {
  const role = getRole();
  const reg = booking.aircraft?.registration || '—';
  const pilot = booking.pilots?.name || '—';
  const date = formatDateUK(booking.flight_date);
  const endDate = booking.end_date && booking.end_date !== booking.flight_date
    ? ` to ${formatDateUK(booking.end_date)}`
    : '';
  const start = (booking.start_time || '').slice(0, 5);
  const end = (booking.end_time || '').slice(0, 5);
  const status = booking.status || '—';
  const purpose = booking.purpose || '—';

  // If editable, open the edit modal
  if (canEditBooking(booking)) {
    openEditBookingModal(booking);
    return;
  }

  // For owners/admins — show detail with approve/reject actions if SUBMITTED
  if ((role === 'owner' || role === 'admin') && booking.status === FLIGHT_STATUS.SUBMITTED) {
    const fields = [
      { name: 'aircraft', label: 'Aircraft', type: 'text', readonly: true },
      { name: 'pilot', label: 'Pilot', type: 'text', readonly: true },
      { name: 'date', label: 'Date', type: 'text', readonly: true },
      { name: 'time', label: 'Time', type: 'text', readonly: true },
      { name: 'purpose', label: 'Purpose', type: 'text', readonly: true },
      { name: 'status', label: 'Current Status', type: 'text', readonly: true },
      { name: 'reject_reason', label: 'Rejection Reason', type: 'textarea' },
    ];

    openDetailModal({
      title: 'Approve / Reject Booking',
      fields,
      values: {
        aircraft: reg,
        pilot,
        date: `${date}${endDate}`,
        time: `${start} – ${end}`,
        purpose,
        status,
        reject_reason: '',
      },
      saveLabel: 'Approve',
      extraButtons: [
        {
          label: 'Reject',
          className: 'btn btn--danger',
          onClick: async () => {
            const form = document.querySelector('.detail-modal__form');
            const reasonEl = form?.elements?.reject_reason;
            const reason = reasonEl?.value?.trim() || '';
            if (!reason) {
              throw new Error('Please provide a reason for rejection.');
            }
            await transitionFlightStatus(booking.id, FLIGHT_STATUS.REJECTED, { reject_reason: reason });
            showMsg('Booking rejected.');
            await loadBookings();
            renderFleetCalendar();
          },
        },
      ],
      onSave: async () => {
        await transitionFlightStatus(booking.id, FLIGHT_STATUS.APPROVED);
        showMsg('Booking approved.');
        await loadBookings();
        renderFleetCalendar();
      },
    });
  } else {
    // Read-only detail view (also shows "Complete Log" for past approved without log)
    const detailFields = [
      { name: 'aircraft', label: 'Aircraft', type: 'text', readonly: true },
      { name: 'pilot', label: 'Pilot', type: 'text', readonly: true },
      { name: 'date', label: 'Date', type: 'text', readonly: true },
      { name: 'time', label: 'Time', type: 'text', readonly: true },
      { name: 'purpose', label: 'Purpose', type: 'text', readonly: true },
      { name: 'status', label: 'Status', type: 'text', readonly: true },
    ];

    const extraButtons = [];
    // If past approved without flight log, offer "Complete Log" button
    const today = new Date().toISOString().split('T')[0];
    if (booking.status === FLIGHT_STATUS.APPROVED && booking.flight_date <= today && !booking.flight_time_mins) {
      extraButtons.push({
        label: 'Complete Log',
        className: 'btn btn--primary',
        onClick: async () => {
          window.location.hash = `#/log-flight?id=${booking.id}`;
        },
      });
    }

    openDetailModal({
      title: 'Booking Details',
      fields: detailFields,
      values: {
        aircraft: reg,
        pilot,
        date: `${date}${endDate}`,
        time: `${start} – ${end}`,
        purpose,
        status,
      },
      readonly: true,
      extraButtons,
    });
  }
}

// ── Flight log for past bookings ──────────────────────

async function showFlightLogForBooking(booking) {
  const reg = booking.aircraft?.registration || '—';
  const pilot = booking.pilots?.name || '—';
  const date = formatDateUK(booking.flight_date);

  // In the unified table, the flight log is on the same row
  if (booking.flight_time_mins != null) {
    // Has a flight log
    const fltTime = `${(booking.flight_time_mins / 60).toFixed(1)} hrs`;

    openDetailModal({
      title: 'Flight Log',
      fields: [
        { name: 'aircraft', label: 'Aircraft', type: 'text', readonly: true },
        { name: 'pilot', label: 'Pilot', type: 'text', readonly: true },
        { name: 'flight_date', label: 'Flight Date', type: 'text', readonly: true },
        { name: 'route', label: 'Route', type: 'text', readonly: true },
        { name: 'flight_time', label: 'Flight Time', type: 'text', readonly: true },
        { name: 'landings', label: 'Landings', type: 'text', readonly: true },
      ],
      values: {
        aircraft: reg,
        pilot,
        flight_date: date,
        route: `${booking.departure || '—'} → ${booking.destination || '—'}`,
        flight_time: fltTime,
        landings: String(booking.landings ?? '—'),
      },
      readonly: true,
    });
  } else {
    // No flight log yet
    const extraButtons = [];
    if (booking.status === FLIGHT_STATUS.APPROVED) {
      extraButtons.push({
        label: 'Complete Log',
        className: 'btn btn--primary',
        onClick: async () => {
          window.location.hash = `#/log-flight?id=${booking.id}`;
        },
      });
    }

    openDetailModal({
      title: 'Flight Log',
      fields: [
        { name: 'aircraft', label: 'Aircraft', type: 'text', readonly: true },
        { name: 'pilot', label: 'Pilot', type: 'text', readonly: true },
        { name: 'date', label: 'Booking Date', type: 'text', readonly: true },
        { name: 'info', label: 'Flight Log', type: 'text', readonly: true },
      ],
      values: { aircraft: reg, pilot, date, info: 'No flight log recorded for this booking.' },
      readonly: true,
      extraButtons,
    });
  }
}

// ── UI helpers ────────────────────────────────────────

function showMsg(text, isError = false) {
  const el = document.getElementById('bookings-msg');
  if (!el) return;
  el.textContent = text;
  el.className = isError ? 'error' : 'success';
  setTimeout(() => {
    el.textContent = '';
    el.className = '';
  }, 5000);
}
